import { useTranslation } from 'react-i18next';
import FaultsRenderer, {
  FaultsRendererValue,
} from '@customer/components/tables/cells/faults/FaultsRenderer';
import {
  useDegreesFormatter,
  useSpeedFormatter,
  useDistanceFormatter,
  DecimalDegreesFormatter,
  EventTypeFormatter,
  useDopFormatter,
  useMillisFormatter,
  MotiveStateFormatter,
  usePercentageFormatter,
} from '@customer/components/tables/formatters/numberFormatters';
import {
  GoodInvalidFormatter,
  OkFailFormatter,
  OnOffFormatter,
  YesNoFormatter,
} from '@customer/components/tables/formatters/booleanFormatters';
import { StringArrayFormatter } from '@customer/components/tables/formatters/objectFormatters';
import { ColDef, ValueGetterParams } from 'ag-grid-community';
import { RawTrack } from '@customer/store/apis/investigationsApi';
import { useCallback, useMemo } from 'react';
import { useAppSelector } from '@customer/store/hooks';
import { RawTrackColumns } from '@customer/store/slices/investigationGuiSlice';
import HealthTooltip from '@customer/components/tables/tooltips/health-tooltip/HealthTooltip';
import DotNetixNameTooltip from '@customer/components/tables/tooltips/dotnetix-name-tooltip/DotNetixNameTooltip';
import { getObjectName } from '@cas/utils/translationHelpers';
import AssetNameRenderer from '@customer/components/tables/cells/asset-name/AssetNameRenderer';

const activeFaultsValueGetter = ({ data }: ValueGetterParams<RawTrack>): FaultsRendererValue => {
  if (data) {
    if (typeof data.SelfTestFaultCount === 'number') {
      if (data.SelfTestFaultCount === 0) {
        return { count: data.SelfTestFaultCount };
      } else {
        // A level of 0 will be treated as unknown.
        let highestLevel = 0;

        if (data.SelfTestFailures?.length) {
          const allLevels: number[] = data.SelfTestFailures.map(({ Level }) => Level).filter(
            (level): level is number => typeof level === 'number',
          );

          // Get the 'highest' level (1 is higher than 3).
          highestLevel = Math.min(...allLevels);

          // Only show the count for the highest level.
          const count = data.SelfTestFailures.filter(f => f.Level === highestLevel).length;

          return { count: count, level: highestLevel };
        }
      }
    }
  }

  return {};
};

export type StrictColDef<T, TColId> = ColDef<T> & {
  colId: TColId;
  headerName: string;
};

type RawTrackColDef = StrictColDef<RawTrack, RawTrackColumns>;

export const useRawTrackColumnDefs = (): RawTrackColDef[] => {
  const { t } = useTranslation();

  const rawTrackCols = useAppSelector(state => state.investigationGui.rawTrackCols);

  const millisFormatter = useMillisFormatter();
  const degreesFormatter = useDegreesFormatter();
  const distanceFormatter = useDistanceFormatter();
  const speedFormatter = useSpeedFormatter();
  const dopFormatter = useDopFormatter();
  const payloadFormatter = usePercentageFormatter(0, true);
  const gradientFormatter = usePercentageFormatter(1, true);

  const otherAssetNameValueGetter = useCallback(
    ({ data }: ValueGetterParams<RawTrack>): [string, boolean] => [
      getObjectName(t, data?.OtherVehicleName, data?.OtherVehicleIsDotNetix ?? false),
      data?.OtherVehicleIsDotNetix ?? false,
    ],
    [t],
  );

  const columnDefs = useMemo<RawTrackColDef[]>(() => {
    // Give every col def a unique colId.
    const columnDefs: RawTrackColDef[] = [
      {
        colId: RawTrackColumns.Time,
        field: 'time',
        headerName: t('Date and Time'),
        valueFormatter: millisFormatter<RawTrack, 'time'>,
        minWidth: 220,
      },
      {
        colId: RawTrackColumns.Asset,
        field: 'AssetName',
        headerName: t('Asset'),
        minWidth: 100,
      },
      {
        colId: RawTrackColumns.ActiveFaults,
        headerName: t('ActiveFaults'),
        valueGetter: activeFaultsValueGetter,
        cellRenderer: FaultsRenderer,
        tooltipField: 'SelfTestFailures',
        tooltipComponent: HealthTooltip,
        minWidth: 110,
      },
      {
        colId: RawTrackColumns.Ignition,
        field: 'Ignition',
        headerName: t('Ignition'),
        valueFormatter: OnOffFormatter<RawTrack, 'Ignition'>,
        cellRenderer: null,
      },
      {
        colId: RawTrackColumns.Speed,
        field: 'Speed',
        headerName: t('Speed'),
        valueFormatter: speedFormatter<RawTrack, 'Speed'>,
        minWidth: 90,
      },
      {
        colId: RawTrackColumns.Heading,
        field: 'Heading',
        headerName: t('Heading'),
        valueFormatter: degreesFormatter<RawTrack, 'Heading'>,
      },
      {
        colId: RawTrackColumns.Forward,
        field: 'Forward',
        headerName: t('Forward'),
        valueFormatter: OnOffFormatter<RawTrack, 'Forward'>,
        cellRenderer: null,
      },
      {
        colId: RawTrackColumns.Reverse,
        field: 'Reverse',
        headerName: t('Reverse'),
        valueFormatter: OnOffFormatter<RawTrack, 'Reverse'>,
        cellRenderer: null,
      },
      {
        colId: RawTrackColumns.GpsFix,
        field: 'ValidFix',
        headerName: t('GpsFix'),
        valueFormatter: GoodInvalidFormatter<RawTrack, 'ValidFix'>,
        cellRenderer: null,
      },
      {
        colId: RawTrackColumns.EventId,
        field: 'EventId',
        headerName: t('EventId'),
        minWidth: 100,
      },
      {
        colId: RawTrackColumns.EventTypeId,
        field: 'EventTypeId',
        headerName: t('EventTypeId'),
        minWidth: 110,
      },
      {
        colId: RawTrackColumns.EventType,
        field: 'EventTypeId',
        headerName: t('EventType'),
        valueFormatter: EventTypeFormatter<RawTrack, 'EventTypeId'>,
        minWidth: 290,
      },
      {
        colId: RawTrackColumns.Audible,
        field: 'Audible',
        headerName: t('Audible'),
        valueFormatter: YesNoFormatter<RawTrack, 'Audible'>,
        cellRenderer: null,
      },
      {
        colId: RawTrackColumns.OtherVehicleDistance,
        field: 'OtherVehicleDistance',
        headerName: t('OtherVehicleDistance'),
        valueFormatter: distanceFormatter<RawTrack, 'OtherVehicleDistance'>,
        minWidth: 120,
      },
      {
        colId: RawTrackColumns.Latitude,
        field: 'Latitude',
        headerName: t('Latitude'),
        valueFormatter: DecimalDegreesFormatter<RawTrack, 'Latitude'>,
        minWidth: 120,
      },
      {
        colId: RawTrackColumns.Longitude,
        field: 'Longitude',
        headerName: t('Longitude'),
        valueFormatter: DecimalDegreesFormatter<RawTrack, 'Latitude'>,
        minWidth: 120,
      },
      {
        colId: RawTrackColumns.Altitude,
        field: 'Altitude',
        headerName: t('Elevation'),
        valueFormatter: distanceFormatter<RawTrack, 'Altitude'>,
        minWidth: 90,
      },
      {
        colId: RawTrackColumns.Geofences,
        field: 'geofenceAt',
        headerName: t('Geofences'),
        valueFormatter: StringArrayFormatter<RawTrack, 'geofenceAt'>,
        minWidth: 200,
      },
      {
        colId: RawTrackColumns.VehiclesInView,
        field: 'vehiclesInView',
        headerName: t('VehiclesInView'),
        valueFormatter: StringArrayFormatter<RawTrack, 'vehiclesInView'>,
        minWidth: 200,
      },
      {
        colId: RawTrackColumns.SatelliteCount,
        field: 'satelliteCount',
        headerName: t('SatelliteCount'),
        minWidth: 120,
      },
      {
        colId: RawTrackColumns.HDOP,
        field: 'Hdop',
        headerName: t('HDOP'),
        valueFormatter: dopFormatter<RawTrack, 'Hdop'>,
      },
      {
        colId: RawTrackColumns.VDOP,
        field: 'Vdop',
        headerName: t('VDOP'),
        valueFormatter: dopFormatter<RawTrack, 'Vdop'>,
      },
      {
        colId: RawTrackColumns.HorizontalAccuracy,
        field: 'HorizontalAccuracy',
        headerName: t('HorizontalAccuracy'),
        valueFormatter: distanceFormatter<RawTrack, 'HorizontalAccuracy'>,
        minWidth: 160,
      },
      {
        colId: RawTrackColumns.HorizontalAccuracySecondary,
        field: 'HorizAccuracySecondaryGNSS',
        headerName: t('HorizontalAccuracySecondary'),
        valueFormatter: distanceFormatter<RawTrack, 'HorizAccuracySecondaryGNSS'>,
        minWidth: 160,
      },
      {
        colId: RawTrackColumns.VerticalAccuracy,
        field: 'VerticalAccuracy',
        headerName: t('VerticalAccuracy'),
        valueFormatter: distanceFormatter<RawTrack, 'VerticalAccuracy'>,
        minWidth: 140,
      },
      {
        colId: RawTrackColumns.GpsAntenna,
        field: 'GpsAntennaStatus',
        headerName: t('GpsAntenna'),
        valueFormatter: OkFailFormatter<RawTrack, 'GpsAntennaStatus'>,
        cellRenderer: null,
        minWidth: 110,
      },
      {
        colId: RawTrackColumns.RuleName,
        field: 'RuleName',
        headerName: t('Rule'),
        minWidth: 120,
      },
      {
        colId: RawTrackColumns.InnerBeamLength,
        field: 'InnerBeamLength',
        headerName: t('InnerBeamLength'),
        valueFormatter: distanceFormatter<RawTrack, 'InnerBeamLength'>,
        minWidth: 150,
      },
      {
        colId: RawTrackColumns.OuterBeamLength,
        field: 'OuterBeamLength',
        headerName: t('OuterBeamLength'),
        valueFormatter: distanceFormatter<RawTrack, 'OuterBeamLength'>,
        minWidth: 150,
      },
      {
        colId: RawTrackColumns.MotiveState,
        field: 'MotiveState',
        headerName: t('MotiveState'),
        valueFormatter: MotiveStateFormatter<RawTrack, 'MotiveState'>,
        minWidth: 150,
      },
      {
        colId: RawTrackColumns.Payload,
        field: 'Payload',
        headerName: t('Payload'),
        valueFormatter: payloadFormatter<RawTrack, 'Payload'>,
      },
      {
        colId: RawTrackColumns.Gradient,
        field: 'Gradient',
        headerName: t('Gradient'),
        valueFormatter: gradientFormatter<RawTrack, 'Gradient'>,
      },
      {
        colId: RawTrackColumns.Level9Active,
        field: 'Level9Active',
        headerName: t('Level9Active'),
        valueFormatter: YesNoFormatter<RawTrack, 'Level9Active'>,
        cellRenderer: null,
        minWidth: 100,
      },
      {
        colId: RawTrackColumns.Level9Action,
        field: 'Level9Action',
        headerName: t('Level9Action'),
        minWidth: 180,
      },
      {
        colId: RawTrackColumns.Level9Level,
        field: 'Level9Level',
        headerName: t('Level9Level'),
        minWidth: 100,
      },
      {
        colId: RawTrackColumns.OtherAssetName,
        field: 'OtherVehicleName',
        headerName: t('ROName'),
        minWidth: 180,
        valueGetter: otherAssetNameValueGetter,
        cellRenderer: AssetNameRenderer,
        tooltipField: 'OtherVehicleIsDotNetix',
        tooltipComponent: DotNetixNameTooltip,
      },
    ];

    // Order the col defs based on their inclusion in the investigation state (visibility and order record).
    const visibleCols: RawTrackColDef[] = [];

    // Set any col defs that are listed in the investigation state to be visible.
    for (let i = 0; i < rawTrackCols.length; i++) {
      const colId = rawTrackCols[i];
      const colDef = columnDefs.find(colDef => colDef.colId === colId);
      if (colDef) {
        visibleCols.push({ ...colDef, hide: false });
      }
    }

    // Make all other col defs hidden.
    const hiddenCols: RawTrackColDef[] = columnDefs
      .filter(({ colId }) => !rawTrackCols.includes(colId))
      .map(colDef => ({ ...colDef, hide: true }));

    // Visible ordered columns first, then default ordered invisible columns.
    return [...visibleCols, ...hiddenCols];
  }, [
    t,
    millisFormatter,
    speedFormatter,
    degreesFormatter,
    distanceFormatter,
    dopFormatter,
    payloadFormatter,
    gradientFormatter,
    otherAssetNameValueGetter,
    rawTrackCols,
  ]);

  return columnDefs;
};

export default useRawTrackColumnDefs;
